// Generic renderer for a single field item: label, control, help, inline error.
// Touch-first: single/multi choice render as tappable option cards, quantity
// fields as +/- steppers. Native select is kept only for long option lists.

import { dateBounds } from './logic';
import { FieldItem, Option, OTHER } from './types';
import { suggestEmail } from './emailSuggest';

/** Options up to this length render as cards; longer lists stay a native select. */
const CARD_SELECT_MAX = 6;

/** True when a single-choice field renders as tappable cards (not a native select). */
export function rendersAsCards(f: FieldItem): boolean {
  return f.type === 'radio' || (f.type === 'select' && (f.options?.length ?? 0) <= CARD_SELECT_MAX);
}

export interface FieldViewProps {
  field: FieldItem;
  value: string | string[] | undefined;
  otherText: string;
  confirmValue: string;
  error?: string;
  confirmError?: string;
  /** Conversational mode: the screen shows the question, so skip the label. */
  hideLabel?: boolean;
  /** Conversational mode: render checkbox options as wrap-row pill chips. */
  chips?: boolean;
  onValue: (id: string, value: string | string[]) => void;
  onOtherText: (id: string, text: string) => void;
  onConfirm: (id: string, text: string) => void;
}

/** Main line of an option card. valueLabel fields lead with the raw value. */
function optionMain(field: FieldItem, o: Option): string {
  if (field.emit === 'valueLabel') return o.value;
  return o.label ?? o.value;
}

/** Secondary line of an option card (valueLabel fields with a distinct label). */
function optionSub(field: FieldItem, o: Option): string | undefined {
  if (field.emit === 'valueLabel' && o.label && o.label !== o.value) return o.label;
  return undefined;
}

/** Flat display text (native select options). */
function optionText(field: FieldItem, o: Option): string {
  const sub = optionSub(field, o);
  return sub ? `${o.value} | ${sub}` : (o.label ?? o.value);
}

/**
 * Tick chip: always present so cards never resize, fills gold when on.
 * TEXT glyph with inline color (state-driven): immune to the style barrier,
 * theme cascade, and the SVG paint failures seen on mobile Safari.
 */
function Tick({ on, square }: { on: boolean; square?: boolean }) {
  return (
    <span className={`tick${on ? ' on' : ''}${square ? ' square' : ''}`} aria-hidden="true">
      <span
        style={{
          color: on ? '#1A1A1A' : 'transparent',
          fontSize: '13px',
          lineHeight: 1,
          fontFamily: 'Arial, sans-serif',
          fontWeight: 700,
          display: 'block',
        }}
      >
        {'✓'}
      </span>
    </span>
  );
}

export function FieldView(props: FieldViewProps) {
  const { field: f, value, otherText, confirmValue, error, confirmError } = props;
  const str = typeof value === 'string' ? value : '';
  const arr = Array.isArray(value) ? value : [];
  const errId = `${f.id}-error`;

  const singleChoiceCards = rendersAsCards(f);

  const renderSingleCards = () => (
    <>
      <div className="cards" role="radiogroup" aria-labelledby={`${f.id}-label`}>
        {f.options?.map((o) => {
          const on = str === o.value;
          const sub = optionSub(f, o);
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={on}
              className={`opt-card${on ? ' on' : ''}`}
              onClick={() => props.onValue(f.id, o.value)}
            >
              <span className="opt-text">
                <span className="opt-main">
                  {optionMain(f, o)}
                  {o.badge && <span className="opt-badge">{o.badge}</span>}
                </span>
                {sub && <span className="opt-sub">{sub}</span>}
              </span>
              <Tick on={on} />
            </button>
          );
        })}
        {f.allowOther && (
          <button
            type="button"
            role="radio"
            aria-checked={str === OTHER}
            className={`opt-card${str === OTHER ? ' on' : ''}`}
            onClick={() => props.onValue(f.id, OTHER)}
          >
            <span className="opt-text">
              <span className="opt-main">Other</span>
            </span>
            <Tick on={str === OTHER} />
          </button>
        )}
      </div>
      {f.allowOther && str === OTHER && (
        <input
          type="text"
          className="other-input"
          value={otherText}
          placeholder="Type your answer"
          aria-label={`${f.label ?? f.id} (other)`}
          onChange={(e) => props.onOtherText(f.id, e.target.value)}
        />
      )}
    </>
  );

  const renderMultiCards = () => (
    <div
      className={`cards${props.chips ? ' chips' : ''}`}
      role="group"
      aria-labelledby={props.hideLabel ? undefined : `${f.id}-label`}
    >
      {f.options?.map((o) => {
        const on = arr.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            className={`opt-card${on ? ' on' : ''}`}
            onClick={() =>
              props.onValue(f.id, on ? arr.filter((x) => x !== o.value) : [...arr, o.value])
            }
          >
            <span className="opt-text">
              <span className="opt-main">
                {optionText(f, o)}
                {o.badge && <span className="opt-badge">{o.badge}</span>}
              </span>
            </span>
            {!props.chips && <Tick on={on} square />}
          </button>
        );
      })}
    </div>
  );

  const renderStars = () => {
    const rating = /^[1-5]$/.test(str) ? Number(str) : 0;
    return (
      <div className="stars" role="radiogroup" aria-labelledby={`${f.id}-label`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const on = n <= rating;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={n === 1 ? '1 star' : `${n} stars`}
              className={`star-btn${on ? ' on' : ''}`}
              onClick={() => props.onValue(f.id, String(n))}
            >
              {/* Text glyph with inline paint: immune to cascade/barrier. */}
              <span
                aria-hidden="true"
                style={{
                  color: on ? '#EBCE00' : '#E4DED3',
                  fontSize: '34px',
                  lineHeight: 1,
                  fontFamily: 'Arial, sans-serif',
                  display: 'block',
                  textShadow: on ? '0 1px 6px rgba(235, 206, 0, 0.35)' : 'none',
                }}
              >
                {'★'}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderStepper = () => {
    const lo = f.min ?? 1;
    const hi = f.max ?? 999;
    const n = /^-?\d+$/.test(str) ? Number(str) : NaN;
    const bump = (delta: number) => {
      const next = Number.isNaN(n) ? lo : Math.min(hi, Math.max(lo, n + delta));
      props.onValue(f.id, String(next));
    };
    return (
      <div className="stepper">
        <button
          type="button"
          className="stepper-btn"
          aria-label={`Decrease ${f.label ?? f.id}`}
          disabled={!Number.isNaN(n) && n <= lo}
          onClick={() => bump(-1)}
        >
          -
        </button>
        <input
          id={f.id}
          className={`stepper-input${error ? ' invalid' : ''}`}
          type="number"
          inputMode="numeric"
          step={1}
          min={f.min}
          max={f.max}
          value={str}
          placeholder="0"
          aria-invalid={!!error}
          aria-describedby={error ? errId : undefined}
          onChange={(e) => props.onValue(f.id, e.target.value)}
        />
        <button
          type="button"
          className="stepper-btn"
          aria-label={`Increase ${f.label ?? f.id}`}
          disabled={!Number.isNaN(n) && n >= hi}
          onClick={() => bump(1)}
        >
          +
        </button>
      </div>
    );
  };

  const control = (() => {
    switch (f.type) {
      case 'select':
        if (singleChoiceCards) return renderSingleCards();
        return (
          <select
            id={f.id}
            className={error ? 'invalid' : undefined}
            value={str}
            aria-invalid={!!error}
            aria-describedby={error ? errId : undefined}
            onChange={(e) => props.onValue(f.id, e.target.value)}
          >
            <option value="">Please select</option>
            {f.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {optionText(f, o)}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return renderSingleCards();

      case 'stars':
        return renderStars();

      case 'checkbox':
        return renderMultiCards();

      case 'textarea':
        return (
          <>
            <textarea
              id={f.id}
              className={error ? 'invalid' : undefined}
              value={str}
              rows={f.rows ?? 4}
              maxLength={f.maxLength}
              placeholder={f.placeholder}
              aria-invalid={!!error}
              aria-describedby={error ? errId : undefined}
              onChange={(e) => props.onValue(f.id, e.target.value)}
            />
            {f.maxLength !== undefined && (
              <div className="char-count">
                {str.length}/{f.maxLength}
              </div>
            )}
          </>
        );

      case 'number':
        // Bounded quantities get a stepper; free numbers (postcode) stay plain.
        if (f.min !== undefined || f.max !== undefined) return renderStepper();
        return (
          <input
            id={f.id}
            className={error ? 'invalid' : undefined}
            type="number"
            inputMode="numeric"
            step={1}
            value={str}
            placeholder={f.placeholder}
            autoComplete={autoCompleteFor(f)}
            aria-invalid={!!error}
            aria-describedby={error ? errId : undefined}
            onChange={(e) => props.onValue(f.id, e.target.value)}
          />
        );

      case 'date': {
        // min/max drive the native picker: out-of-window days are greyed out,
        // so the customer cannot pick a job we no longer hold.
        const bounds = dateBounds(f);
        return (
          <input
            id={f.id}
            className={error ? 'invalid' : undefined}
            type="date"
            value={str}
            min={bounds.min}
            max={bounds.max}
            placeholder={f.placeholder}
            aria-invalid={!!error}
            aria-describedby={error ? errId : undefined}
            onChange={(e) => props.onValue(f.id, e.target.value)}
          />
        );
      }

      default:
        return (
          <input
            id={f.id}
            className={error ? 'invalid' : undefined}
            type={f.type}
            value={str}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            autoComplete={autoCompleteFor(f)}
            inputMode={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : undefined}
            autoCapitalize={NAME_LIKE.has(f.id) ? 'words' : undefined}
            autoCorrect={f.type === 'email' ? 'off' : undefined}
            spellCheck={f.type === 'email' ? false : undefined}
            enterKeyHint="next"
            aria-invalid={!!error}
            aria-describedby={error ? errId : undefined}
            onChange={(e) => props.onValue(f.id, e.target.value)}
          />
        );
    }
  })();

  return (
    <div className="field" data-field={f.id}>
      {f.label && !props.hideLabel && (
        <label className="field-label" id={`${f.id}-label`} htmlFor={f.id}>
          {f.label}
          {f.required && (
            <span className="req" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {f.help && !props.hideLabel && <div className="field-help">{f.help}</div>}
      {control}
      {f.type === 'email' && (() => {
        // "Did you mean …?" (2026-10-13): one tap swaps in the fix.
        const fix = suggestEmail(str);
        return fix ? (
          <div className="email-suggest" role="status">
            Did you mean{' '}
            <button type="button" className="email-suggest-btn"
              onClick={() => props.onValue(f.id, fix)}>
              {fix}
            </button>
            ?
          </div>
        ) : null;
      })()}
      {error && (
        <div className="field-error" id={errId} role="alert">
          {error}
        </div>
      )}

      {f.type === 'email' && f.confirm && (
        <div className="field confirm-field" data-field={`${f.id}__confirm`}>
          <label className="field-label" htmlFor={`${f.id}-confirm`}>
            Confirm Email
            {f.required && (
              <span className="req" aria-hidden="true">
                *
              </span>
            )}
          </label>
          <input
            id={`${f.id}-confirm`}
            className={confirmError ? 'invalid' : undefined}
            type="email"
            value={confirmValue}
            autoComplete="off"
            aria-invalid={!!confirmError}
            onChange={(e) => props.onConfirm(f.id, e.target.value)}
          />
          {confirmError && (
            <div className="field-error" role="alert">
              {confirmError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Fields whose values are proper nouns: capitalise each word on mobile. */
const NAME_LIKE = new Set(['firstName', 'lastName', 'companyName', 'suburb', 'streetAddress']);

function autoCompleteFor(f: FieldItem): string | undefined {
  switch (f.id) {
    case 'firstName':
      return 'given-name';
    case 'lastName':
      return 'family-name';
    case 'companyName':
      return 'organization';
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'streetAddress':
      return 'street-address';
    case 'suburb':
      return 'address-level2';
    case 'postcode':
      return 'postal-code';
    default:
      return undefined;
  }
}
